import config from '../config';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { commitVaultRecords, readVaultRecord } from './vaultStorage';

const KDF_ITERATIONS = 600000;
const LOCK_KEY = 'senaOfflineVaultLockRevision';
const SESSION_TICKET_KEY = 'senaOfflineVaultSessionTicket';
const keys = new Map();
let channel;
let listening = false;

const bytesToBase64 = value => {
  let binary = '';
  new Uint8Array(value).forEach(byte => { binary += String.fromCharCode(byte); });
  return btoa(binary);
};
const base64ToBytes = value => Uint8Array.from(atob(value), char => char.charCodeAt(0));
const encode = value => new TextEncoder().encode(value);
const ownerKeyOf = owner => typeof owner === 'string' ? owner : owner?.ownerKey;
const revision = () => localStorage.getItem(LOCK_KEY) || '';

function vaultError(message, code = 'OFFLINE_VAULT_LOCKED') {
  const error = new Error(message);
  error.code = code;
  return error;
}

function requireOwner(owner = getOfflineOwnerIdentity()) {
  const current = getOfflineOwnerIdentity();
  if (!current || current.ownerKey !== ownerKeyOf(owner)) throw vaultError('Sign in to the same account and practice to open these device records.');
  return current;
}

function notify() { window.dispatchEvent(new CustomEvent('vela:offline-vault-changed')); }

function forgetKeys() { keys.clear(); sessionStorage.removeItem(SESSION_TICKET_KEY); notify(); }

function installLockListeners() {
  if (listening) return;
  listening = true;
  window.addEventListener('storage', event => {
    if (event.key === LOCK_KEY) forgetKeys();
    if (event.key === 'velaToken') {
      const owner = getOfflineOwnerIdentity();
      for (const key of keys.keys()) if (key !== owner?.ownerKey) keys.delete(key);
      notify();
    }
  });
  window.addEventListener('vela:access-context-changed', forgetKeys);
  // Visibility/sleep do not lock an ongoing labor session. A newly created
  // document naturally has no in-memory keys and requires its own unlock.
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('sena-offline-vault-lock');
    channel.onmessage = event => { if (event.data?.type === 'lock') forgetKeys(); };
  }
}

export function lockOfflineVault() {
  installLockListeners();
  localStorage.setItem(LOCK_KEY, `${Date.now()}:${crypto.randomUUID()}`);
  forgetKeys();
  channel?.postMessage({ type: 'lock' });
}

export function isOfflineVaultUnlocked(owner = getOfflineOwnerIdentity()) {
  const key = ownerKeyOf(owner);
  const entry = key && keys.get(key);
  return Boolean(entry && entry.revision === revision() && getOfflineOwnerIdentity()?.ownerKey === key);
}

export async function getVaultStatus(owner = getOfflineOwnerIdentity()) {
  if (!owner) return { enrolled: false, unlocked: false, recoveryEnrolled: false };
  installLockListeners();
  const record = await readVaultRecord('vaults', ownerKeyOf(owner));
  return {
    enrolled: Boolean(record),
    unlocked: Boolean(record && isOfflineVaultUnlocked(owner)),
    recoveryEnrolled: Boolean(record?.recoveryEnrolled),
    vaultId: record?.vaultId || null,
  };
}

function validatePassphrase(passphrase) {
  if (typeof passphrase !== 'string' || passphrase.length < 12) throw new Error('Use a device passphrase of at least 12 characters.');
}

async function wrappingKey(passphrase, salt, iterations) {
  const material = await crypto.subtle.importKey('raw', encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, material,
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

async function wrapRawKey(rawKey, passphrase, ownerKey, vaultId) {
  validatePassphrase(passphrase);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await wrappingKey(passphrase, salt, KDF_ITERATIONS);
  const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv,
    additionalData: encode(`sena-vault-v1:${ownerKey}:${vaultId}`) }, key, rawKey);
  return { salt: bytesToBase64(salt), iv: bytesToBase64(iv), wrappedKey: bytesToBase64(wrapped), iterations: KDF_ITERATIONS };
}

async function recoveryRequest(action, body, owner) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), action.endsWith('session') ? 5000 : 15000);
  try {
  const response = await fetch(`${config.api.authUrl}/offline-vault/recovery/${action}`, {
    method: 'POST', headers: { Authorization: `Bearer ${owner.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body), signal: controller.signal,
  });
  requireOwner(owner);
  if (!response.ok) {
    const error = new Error(response.status === 401
      ? 'Sign in again before recovering your device passphrase. Your device records are unchanged.'
      : 'Device recovery could not be set up. Reconnect and try again; your existing records are unchanged.');
    error.code = response.status === 401 ? 'FRESH_SIGN_IN_REQUIRED' : 'OFFLINE_RECOVERY_UNAVAILABLE';
    throw error;
  }
  return await response.json();
  } finally { clearTimeout(timeout); }
}

async function rememberKey(rawKey, record, expectedRevision, authorizeSession = true) {
  requireOwner(record.id);
  if (expectedRevision !== revision()) throw vaultError('The device was locked. Unlock it again to continue.');
  const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['encrypt', 'decrypt']);
  requireOwner(record.id);
  if (expectedRevision !== revision()) throw vaultError('The device was locked. Unlock it again to continue.');
  keys.set(record.id, { key, vaultId: record.vaultId, revision: expectedRevision });
  notify();
  if (authorizeSession && navigator.onLine !== false) {
    // Only an opaque, bearer-bound server ticket is retained across reloads.
    // Neither the vault key nor passphrase is written to tab storage.
    const owner = requireOwner(record.id);
    try {
      const proofKey = await crypto.subtle.importKey('raw', rawKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
      const proof = bytesToBase64(await crypto.subtle.sign('HMAC', proofKey, encode(`sena-vault-session-v1:${record.vaultId}:${owner.token}`)));
      const result = await recoveryRequest('authorize-session', { vaultId: record.vaultId, proof }, owner);
      if (result.ticket && expectedRevision === revision() && isOfflineVaultUnlocked(owner))
        sessionStorage.setItem(SESSION_TICKET_KEY, JSON.stringify({ ownerKey: owner.ownerKey, vaultId: record.vaultId,
          revision: expectedRevision, ticket: result.ticket, expiresAt: result.expiresAt }));
    } catch { /* Unlock succeeded; unavailable session service only means next reload needs the passphrase. */ }
  }
}

export async function enrollOfflineVault(passphrase) {
  validatePassphrase(passphrase);
  const owner = requireOwner();
  if (owner.expiresAt <= Date.now()) throw new Error('Sign in while online before setting up device charting.');
  installLockListeners();
  const expectedRevision = revision();
  if (await readVaultRecord('vaults', owner.ownerKey)) throw new Error('This device already has a passphrase for this account and practice. Unlock or recover it.');
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  try {
    const vaultId = crypto.randomUUID();
    const wrapped = await wrapRawKey(rawKey, passphrase, owner.ownerKey, vaultId);
    await recoveryRequest('protect', { vaultId, keyBase64: bytesToBase64(rawKey) }, owner);
    requireOwner(owner);
    const record = { id: owner.ownerKey, vaultId, version: 1, ...wrapped, recoveryEnrolled: true, createdAt: Date.now() };
    await commitVaultRecords([{ store: 'vaults', add: true, record }]);
    await rememberKey(rawKey, record, expectedRevision);
    navigator.storage?.persist?.().catch(() => {});
    return getVaultStatus(owner);
  } finally { rawKey.fill(0); }
}

export async function unlockOfflineVault(passphrase) {
  const owner = requireOwner();
  installLockListeners();
  const expectedRevision = revision();
  const record = await readVaultRecord('vaults', owner.ownerKey);
  if (!record) throw vaultError('Set up a device passphrase while online first.', 'OFFLINE_VAULT_NOT_ENROLLED');
  let rawKey;
  try {
    const key = await wrappingKey(passphrase, base64ToBytes(record.salt), record.iterations);
    rawKey = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: base64ToBytes(record.iv),
      additionalData: encode(`sena-vault-v1:${owner.ownerKey}:${record.vaultId}`) }, key, base64ToBytes(record.wrappedKey)));
  } catch { throw new Error('The device passphrase did not unlock these records. Try again or recover it after signing in.'); }
  try { await rememberKey(rawKey, record, expectedRevision); }
  finally { rawKey.fill(0); }
  return getVaultStatus(owner);
}

let resuming = null;
export async function resumeOfflineVaultSession() {
  if (resuming) return resuming;
  resuming = (async () => {
    const owner = getOfflineOwnerIdentity();
    if (!owner || owner.expiresAt <= Date.now() || navigator.onLine === false) return false;
    if (isOfflineVaultUnlocked(owner)) return true;
    let ticket;
    try { ticket = JSON.parse(sessionStorage.getItem(SESSION_TICKET_KEY) || 'null'); } catch { return false; }
    if (!ticket || ticket.ownerKey !== owner.ownerKey || ticket.revision !== revision() || ticket.expiresAt * 1000 <= Date.now()) return false;
    const record = await readVaultRecord('vaults', owner.ownerKey);
    if (record?.vaultId !== ticket.vaultId) return false;
    try {
      const response = await recoveryRequest('resume-session', { vaultId: ticket.vaultId, ticket: ticket.ticket }, owner);
      if (response.vaultId !== record.vaultId) return false;
      const rawKey = base64ToBytes(response.keyBase64);
      try {
        if (rawKey.length !== 32) return false;
        await rememberKey(rawKey, record, ticket.revision, false);
        return true;
      } finally { rawKey.fill(0); }
    } catch { return false; }
  })();
  try { return await resuming; } finally { resuming = null; }
}

export async function recoverOfflineVault(newPassphrase) {
  validatePassphrase(newPassphrase);
  const owner = requireOwner();
  const expectedRevision = revision();
  const record = await readVaultRecord('vaults', owner.ownerKey);
  if (!record?.recoveryEnrolled) throw new Error('Online recovery is not available for this vault. Its existing records have been kept.');
  const recovered = await recoveryRequest('recover', { vaultId: record.vaultId }, owner);
  if (recovered.vaultId !== record.vaultId) throw new Error('Recovery returned a different vault. Your device records are unchanged.');
  const rawKey = base64ToBytes(recovered.keyBase64);
  try {
    if (rawKey.length !== 32) throw new Error('Recovery returned an invalid key. Your device records are unchanged.');
    const wrapped = await wrapRawKey(rawKey, newPassphrase, owner.ownerKey, record.vaultId);
    requireOwner(owner);
    if (expectedRevision !== revision()) throw vaultError('The device was locked. Try recovery again.');
    await commitVaultRecords([{ store: 'vaults', record: { ...record, ...wrapped } }]);
    await rememberKey(rawKey, record, expectedRevision);
  } finally { rawKey.fill(0); }
  return getVaultStatus(owner);
}

function requireKey(owner) {
  const identity = requireOwner(owner);
  const entry = keys.get(identity.ownerKey);
  if (!entry || entry.revision !== revision()) throw vaultError('Unlock device charting to read or save protected device records.');
  return { identity, entry };
}

export async function encryptVaultPayload(payload, owner = getOfflineOwnerIdentity()) {
  const { identity, entry } = requireKey(owner);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const envelope = { version: 2, vaultId: entry.vaultId, ownerKey: identity.ownerKey, iv: bytesToBase64(iv) };
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv,
    additionalData: encode(`sena-payload-v2:${identity.ownerKey}:${entry.vaultId}`) }, entry.key, encode(JSON.stringify(payload)));
  requireKey(owner);
  return { ...envelope, data: bytesToBase64(data) };
}

export async function decryptVaultPayload(envelope, owner = getOfflineOwnerIdentity()) {
  const { identity, entry } = requireKey(owner);
  if (envelope?.version !== 2 || envelope.ownerKey !== identity.ownerKey || envelope.vaultId !== entry.vaultId) {
    throw vaultError('These device records belong to another account or practice.');
  }
  const data = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: base64ToBytes(envelope.iv),
    additionalData: encode(`sena-payload-v2:${identity.ownerKey}:${entry.vaultId}`) }, entry.key, base64ToBytes(envelope.data));
  requireKey(owner);
  return JSON.parse(new TextDecoder().decode(data));
}
